import React, { useState, useEffect } from "react";
import { Form, Button } from "react-bootstrap";
import Select, { CSSObjectWithLabel, StylesConfig } from "react-select";
import {
  generateOutlineAiResponse,
  GenerateOutlineResponse,
} from "../../services/getAiResponse";
import { useStore, Outline } from "./store/store";
import { Box } from "./outlineGeneratorStyle";
import { isInIframe } from "../../utils/is-in-iframe";
import { ToolsHeader } from "../components/ToolsHeader";

interface Option {
  value: string;
  label: string;
}

const languageOptions: Option[] = [
  { value: "english", label: "English" },
  { value: "spanish", label: "Spanish" },
  { value: "french", label: "French" },
  { value: "german", label: "German" },
];

const toneOptions: Option[] = [
  { value: "formal", label: "Formal" },
  { value: "professional", label: "Professional" },
  { value: "casual", label: "Casual" },
  { value: "friendly", label: "Friendly" },
];

const structureOptions: Option[] = [
  { value: "3", label: "3 Sections" },
  { value: "5", label: "5 Sections" },
  { value: "10", label: "10 Sections" },
];

const OutlineGenerator: React.FC = () => {
  const [topic, setTopic] = useState("");
  const [blogTopic, setBlogTopic] = useState("");
  const [language, setLanguage] = useState<Option | null>({
    value: "english",
    label: "English",
  });
  const [tone, setTone] = useState<Option | null>({
    value: "formal",
    label: "Formal",
  });
  const [structure, setStructure] = useState<Option | null>({
    value: "3",
    label: "3 Sections",
  });
  const [loadingOutlines, setLoadingOutlines] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const outlines = useStore((state) => state.outlines);
  const addOutline = useStore((state) => state.addOutline);

  const handleTopicChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setTopic(e.target.value);
  const handleBlogTopicChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setBlogTopic(e.target.value);
  const handleLanguageChange = (option: Option | null) => setLanguage(option);
  const handleToneChange = (option: Option | null) => setTone(option);
  const handleStructureChange = (option: Option | null) => setStructure(option);

  const handleGenerateOutlines = async () => {
    if (!topic || !blogTopic || !tone || !structure || !language) {
      setErrorMessage("Please fill in all fields.");
      return;
    }

    setErrorMessage(null);
    setLoadingOutlines(true);

    try {
      const response = await generateOutlineAiResponse(
        topic,
        blogTopic,
        language.value,
        tone.value,
        parseInt(structure.value, 10)
      );
      const metadata = getMetadata(response, tone.value, language.value);
      const newOutline: Outline = { ...response, metadata };
      addOutline(newOutline);
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: "setHeight", height }, "*");
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Failed to generate outline. Please try again."
      );
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: "setHeight", height }, "*");
    } finally {
      setLoadingOutlines(false);
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: "setHeight", height }, "*");
    }
  };

  const handleExport = (outline: GenerateOutlineResponse) => {
    const outlineText = outlineToString(outline);
    const blob = new Blob([outlineText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${outline.title}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopy = (outline: GenerateOutlineResponse, index: number) => {
    const outlineText = outlineToString(outline);
    navigator.clipboard.writeText(outlineText).then(
      () => {
        setCopiedIndex(index);
      },
      (err) => console.error("Failed to copy outline:", err)
    );
  };

  useEffect(() => {
  document.body.classList.add("outline-generator-active");

  return () => {
    document.body.classList.remove("outline-generator-active");
  };
}, []);


  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [errorMessage, outlines]);
  useEffect(() => {
    if (copiedIndex !== null) {
      const timer = setTimeout(() => setCopiedIndex(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [copiedIndex]);

  const selectStyles: StylesConfig<Option, false> = {
    container: (provided: CSSObjectWithLabel) => ({
      ...provided,
      width: "150px",
    }),
    menuPortal: (base: CSSObjectWithLabel) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  return (
    <Box className="outline-generator-page">
      {!isInIframe && (
        <ToolsHeader
          title="Free AI Outline Generator"
          description={`Input your rough ideas and transition from a blank page to an
          organized, well-structured outline in minutes.`}
        />
      )}
      {/* <div className="text-left mb-4">
        <h3 className="fw-bold">Free AI Outline Generator</h3>
        <p className="text-muted mb-0">
          Input your rough ideas and transition from a blank page to an
          organized, well-structured outline in minutes.
        </p>
      </div> */}
      <div
        className={`border rounded mainFormOutlineGen ${
          isInIframe && "interDisplay"
        }`}
        style={{ backgroundColor: "#f8f9fa" }}
      >
        {errorMessage && (
          <div className="alert alert-danger" role="alert">
            {errorMessage}
          </div>
        )}

        <h5 className="fw-bold mb-2">What is the topic of your blog post?</h5>
        <Form.Group controlId="blogTopicInput" className="mb-3">
          <Form.Control
            type="text"
            value={blogTopic}
            onChange={handleBlogTopicChange}
            placeholder="e.g. How to make a website"
            style={{ borderColor: "#8CC540", borderWidth: "2px" }}
          />
        </Form.Group>

        <div className="flex-grow-1">
          <Form.Group controlId="topicInput">
            <Form.Label>Important points to include in your content</Form.Label>
            <Form.Control
              as="textarea"
              rows={8}
              value={topic}
              onChange={handleTopicChange}
              placeholder="e.g. How to choose a CMS and Where to host a blog"
              style={{
                borderColor: "#8CC540",
                borderWidth: "2px",
                paddingTop: "13px",
              }}
            />
          </Form.Group>

          <div className="d-flex align-items-end mt-3 gap-2 flex-wrap">
            <Form.Group controlId="languageSelect">
              <Form.Label className="fw-bold">Language</Form.Label>
              <Form.Select
                value={language?.value || ""}
                style={{ width: "130px" }}
                onChange={(e) => {
                  const findLang = languageOptions.find(
                    (X) => X.value === e.target.value
                  );
                  if (findLang) {
                    setLanguage(findLang);
                  }
                }}
              >
                {languageOptions.map((x, i) => (
                  <option value={x.value} key={i}>
                    <div>{x.label}</div>
                  </option>
                ))}
              </Form.Select>
              {/* <Select
                options={languageOptions}
                value={language}
                onChange={handleLanguageChange}
                className="basic-single"
                classNamePrefix="select"
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPlacement="auto"
              /> */}
            </Form.Group>

            <Form.Group controlId="toneSelect">
              <Form.Label className="fw-bold">Tone</Form.Label>
              <Form.Select
                value={tone?.value || ""}
                style={{ width: "130px" }}
                onChange={(e) => {
                  const findTone = toneOptions.find(
                    (X) => X.value === e.target.value
                  );
                  if (findTone) {
                    setTone(findTone);;
                  }
                }}
              >
                {toneOptions.map((x, i) => (
                  <option value={x.value} key={i}>
                    <div>{x.label}</div>
                  </option>
                ))}
              </Form.Select>
              {/* <Select
                options={toneOptions}
                value={tone}
                onChange={handleToneChange}
                className="basic-single"
                classNamePrefix="select"
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPlacement="auto"
              /> */}
            </Form.Group>

            <Form.Group controlId="structureSelect">
              <Form.Label className="fw-bold">Structure</Form.Label>
              <Form.Select
                value={structure?.value || ""}
                style={{ width: "130px" }}
                onChange={(e) => {
                  const findStructure = structureOptions.find(
                    (X) => X.value === e.target.value
                  );
                  if (findStructure) {
                    setStructure(findStructure);
                  }
                }}
              >
                {structureOptions.map((x, i) => (
                  <option value={x.value} key={i}>
                    <div>{x.label}</div>
                  </option>
                ))}
              </Form.Select>
              {/* <Select
                options={structureOptions}
                value={structure}
                onChange={handleStructureChange}
                className="basic-single"
                classNamePrefix="select"
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPlacement="auto"
              /> */}
            </Form.Group>

            <Button
              variant="outline-secondary"
              onClick={() =>
                outlines.length > 0 &&
                handleExport(outlines[outlines.length - 1])
              }
              style={{ height: "38px", padding: "0 15px" }}
              disabled={outlines.length === 0}
            >
              📤 Export
            </Button>

            <div className="flex-grow-1 d-flex justify-content-end">
              <Button
                variant="warning"
                onClick={handleGenerateOutlines}
                disabled={loadingOutlines}
                style={{
                  backgroundColor: "#8CC540",
                  borderColor: "#8CC540",
                  color: "white",
                  height: "38px",
                }}
              >
                {loadingOutlines
                  ? "Creating Outlines..."
                  : "📝 Generate Outlines"}
              </Button>
            </div>
          </div>
        </div>

        {/* Render all generated outlines */}
        {outlines.length > 0 && (
          <div className="mt-4">
            <h4 className="fw-bold mb-3">Generated Outlines</h4>
            {outlines.map((outline: Outline, index: number) => (
              <div
                key={index}
                className="mt-4 border rounded mainFormOutlineGen"
                style={{ backgroundColor: "#fff" }}
              >
                <div className="metadata mb-2 text-muted">
                  {outline.metadata.characters} characters ·{" "}
                  {outline.metadata.words} words · {outline.metadata.tone} ·{" "}
                  {outline.metadata.language}
                </div>
                <h4 className="fw-bold">{outline.title}</h4>
                <ol type="I">
                  {outline.sections.map(
                    (
                      section: { heading: string; subpoints: string[] },
                      secIndex: number
                    ) => (
                      <li key={secIndex} className="mb-3">
                        <strong>
                          {section.heading.replace(/^[IVXLCDM]+\.\s/, "")}
                        </strong>
                        <ol
                          type="A"
                          style={{ paddingLeft: "20px", marginTop: "5px" }}
                        >
                          {section.subpoints.map(
                            (subpoint: string, subIndex: number) => (
                              <li key={subIndex}>
                                {subpoint.replace(/^[A-C]\.\s/, "")}
                              </li>
                            )
                          )}
                        </ol>
                      </li>
                    )
                  )}
                </ol>
                <div className="d-flex align-items-center">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleCopy(outline, index)}
                  >
                    📋 Copy
                  </Button>
                  {copiedIndex === index && (
                    <span className="ms-2 text-success">Copied</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Box>
  );
};

// Helper functions
const outlineToString = (outline: GenerateOutlineResponse): string => {
  return `${outline.title}\n\n${outline.sections
    .map(
      (section) =>
        `${section.heading}\n${section.subpoints
          .map((sub) => `   ${sub}`)
          .join("\n")}`
    )
    .join("\n\n")}`;
};

const getMetadata = (
  outline: GenerateOutlineResponse,
  tone: string,
  language: string
) => {
  const text = outlineToString(outline);
  const characters = text.length;
  const words = text.split(/\s+/).filter((word) => word.length > 0).length;
  const capitalizedTone = tone.charAt(0).toUpperCase() + tone.slice(1);
  const capitalizedLanguage =
    language.charAt(0).toUpperCase() + language.slice(1);
  return {
    characters,
    words,
    tone: capitalizedTone,
    language: capitalizedLanguage,
  };
};

export default OutlineGenerator;
